import CopyableText from '@/components/LabelWithdescription/CopyableText';
import { CallLog, CallScore, CallSentiment } from '@/interfaces/ILogs';
import SentimentsPanel from './SentimentGraph';
import { FileText, MessageSquare } from 'lucide-react';
import { safeJSONParse } from '@/utils/helper';

export const CallDetails = ({ callLog }: { callLog: CallLog | null }) => {
	const score: CallScore | null = safeJSONParse(callLog?.callScore, null);
	const sentiment: CallSentiment | null = safeJSONParse(callLog?.callSentiments, null);

	const scriptMessages = callLog?.transcript
		?.split('\n')
		?.map((message: string, index: number) => {
			if (message?.startsWith('assistant@')) {
				return {
					key: index,
					type: 'assistant',
					sender: 'Assistant',
					message: message.replace('assistant@', ''),
				};
			} else if (message?.startsWith('user@')) {
				return { key: index, type: 'user', sender: 'User', message: message.replace('user@', '') };
			} else if (message?.startsWith('system@')) {
				return { key: index, type: 'system', message: message.replace('system@', '') };
			}
		})
		?.filter(Boolean);

	return (
		<div className="max-h-[65vh] overflow-y-auto py-6 px-1 bg-gray-50/50 rounded-lg">
			{/* Summary Section */}
			{callLog?.callSummary && (
				<div className="mb-8 animate-fade-in">
					<div className="flex items-center justify-between mb-3">
						<div className="flex items-center gap-2">
							<div className="p-1.5 rounded-md bg-violet-100">
								<FileText className="h-4 w-4 text-violet-600" />
							</div>
							<h3 className="font-semibold text-gray-800">Summary</h3>
						</div>
					</div>
					<div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
						<p className="text-gray-700 leading-relaxed">{callLog.callSummary}</p>
					</div>
				</div>
			)}

			<div className="space-y-3 px-1">
				<div className="flex items-center justify-between mb-3">
					<div className="flex items-center gap-2">
						<div className="p-1.5 rounded-md bg-blue-100">
							<MessageSquare className="h-4 w-4 text-blue-600" />
						</div>
						<h3 className="font-semibold text-gray-800">Call Transcript</h3>
					</div>
				</div>
				{scriptMessages?.map((item) => {
					if (!item) {
						return null;
					}

					if (item.type === 'system') {
						return (
							<div key={item.key} className="flex justify-center py-2">
								<div className="inline-flex items-center px-4 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
									{item.message}
								</div>
							</div>
						);
					}

					const isUser = item.type === 'user';

					return (
						<div key={item.key} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
							<div
								className={`max-w-[80%] rounded-2xl px-4 py-1.5 shadow-sm animate-fade-in ${
									isUser
										? 'bg-blue-600 text-white rounded-tr-none'
										: 'bg-white border border-gray-200 rounded-tl-none'
								}`}
								style={{ animationDelay: `${item.key * 0.05}s` }}
							>
								<div className="flex items-center gap-2 ">
									<span
										className={`text-xs font-semibold ${isUser ? 'text-blue-100' : 'text-gray-500'}`}
									>
										{item.sender}
									</span>
								</div>
								<p
									className={`text-sm leading-relaxed mb-0 ${isUser ? 'text-white' : 'text-gray-700'}`}
								>
									{item.message}
								</p>
							</div>
						</div>
					);
				})}
			</div>

			{score && (
				<div className="flex flex-col mb-8 gap-4 bg-white p-6 rounded-lg shadow-md">
					<div className="flex items-center gap-3">
						<span className="font-bold text-gray-900 tracking-wide">CALL SCORE</span>
						<CopyableText text={score?.value?.toString()} hideText />
					</div>
					<span className="text-gray-800 text-3xl font-semibold">{score?.value}</span>
					<span className="text-gray-700 text-base leading-relaxed">{score?.description}</span>
				</div>
			)}

			{sentiment && (
				<div className="flex flex-col mb-8 gap-4 bg-white p-6 rounded-lg shadow-md">
					<div className="flex items-center gap-3">
						<span className="font-bold text-gray-900 tracking-wide">CALL SENTIMENTS</span>
					</div>
					<SentimentsPanel sentiments={sentiment} />
					<span className="text-gray-700 text-base leading-relaxed">{sentiment?.description}</span>
				</div>
			)}
		</div>
	);
};
